"use strict";
/*
 * Copyright 2023, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.offsetOverlappingLines = exports.getLinesInView = exports.OFFSET_WIDTH = void 0;
const turf_1 = require("@turf/turf");
exports.OFFSET_WIDTH = 3; // Offset of each line in meters
/**
 * Obtains the lines from a paths layer that have at least one coordinate inside the viewport.
 * @param bounds - a bounding box Polygon that represents the viewport's coordinates
 * @param layer - the paths layer that we want to get the visible lines from
 * @return A collection of the lines that are in the viewport
 */
const getLinesInView = (bounds, layer) => {
    const features = layer.features;
    const linesInView = { type: 'FeatureCollection', features: [] };
    for (let i = 0; i < features.length; i++) {
        for (let j = 0; j < features[i].geometry.coordinates.length; j++) {
            if (isInBounds(bounds, features[i].geometry.coordinates[j])) {
                linesInView.features.push(features[i]);
                break;
            }
        }
    }
    return linesInView;
};
exports.getLinesInView = getLinesInView;
const isInBounds = (bounds, coord) => {
    return (0, turf_1.booleanPointInPolygon)(coord, bounds);
};
/**
 * Offset overlapping lines when multiple lines use the same road or segment.
 * This is to be able to visually follow where each line goes.
 * @param layerData GeoJSON data containing the lines to offset (will be modified)
 */
const offsetOverlappingLines = (layerData_1, ...args_1) => __awaiter(void 0, [layerData_1, ...args_1], void 0, function* (layerData, isCancelled = false) {
    try {
        const overlapMap = yield findOverlappingLines(layerData, isCancelled);
        const overlapArray = manageOverlappingSegmentsData(overlapMap, layerData);
        yield applyOffset(overlapArray, layerData, isCancelled);
        cleanLines(layerData);
        return;
    }
    catch (e) {
        return Promise.reject(e);
    }
});
exports.offsetOverlappingLines = offsetOverlappingLines;
const findOverlappingLines = (layerData_1, ...args_1) => __awaiter(void 0, [layerData_1, ...args_1], void 0, function* (layerData, isCancelled = false) {
    if (isCancelled && isCancelled()) {
        return Promise.reject('Cancelled');
    }
    const features = layerData.features;
    // The map contains the feature and a set of numbers
    // The feature is the segment concerned by the overlap
    // The set of numbers is a set that contains the IDs of every single line concerned by the overlap on that segment
    let overlapMap = new Map();
    for (let i = 0; i < features.length - 1; i++) {
        if (i % 20 === 0) {
            if (isCancelled && isCancelled()) {
                return Promise.reject('Cancelled');
            }
        }
        for (let j = i + 1; j < features.length; j++) {
            const overlap = (0, turf_1.lineOverlap)((0, turf_1.lineString)(features[i].geometry.coordinates), (0, turf_1.lineString)(features[j].geometry.coordinates));
            if (overlap.features.length === 0)
                continue;
            if (j % 20 === 0) {
                yield new Promise((resolve) => setTimeout(() => {
                    overlapMap = fillOverlapMap(overlap, overlapMap, i, j);
                    resolve();
                }, 0));
            }
            else {
                overlapMap = fillOverlapMap(overlap, overlapMap, i, j);
            }
        }
    }
    return Promise.resolve(overlapMap);
});
const fillOverlapMap = (overlap, overlapMap, indexI, indexJ) => {
    var _a;
    for (const segment of overlap.features) {
        const overlapStr = JSON.stringify(segment);
        if (!overlapMap.has(overlapStr))
            overlapMap.set(overlapStr, new Set());
        (_a = overlapMap.get(overlapStr)) === null || _a === void 0 ? void 0 : _a.add(indexI).add(indexJ);
    }
    return overlapMap;
};
const manageOverlappingSegmentsData = (overlapMap, layerData) => {
    const overlapArray = [];
    overlapMap.forEach((value, key) => {
        const segmentDirections = [];
        const keyGeojson = JSON.parse(key);
        // For each line, add the direction of the overlap segment
        value.forEach((index) => {
            const data = layerData.features[index];
            const coordinates = keyGeojson.geometry.coordinates;
            const firstPoint = coordinates[0];
            const lastPoint = coordinates[coordinates.length - 1];
            for (let i = 0; i < data.geometry.coordinates.length; i++) {
                const actualPoint = data.geometry.coordinates[i];
                if (actualPoint[0] === firstPoint[0] && actualPoint[1] === firstPoint[1]) {
                    segmentDirections.push(true);
                    break;
                }
                else if (actualPoint[0] === lastPoint[0] && actualPoint[1] === lastPoint[1]) {
                    segmentDirections.push(false);
                    break;
                }
            }
        });
        const overlap = {
            geoData: keyGeojson,
            crossingLines: Array.from(value),
            directions: segmentDirections
        };
        overlapArray.push(overlap);
    });
    return overlapArray;
};
const applyOffset = (overlapArray_1, layerData_1, ...args_1) => __awaiter(void 0, [overlapArray_1, layerData_1, ...args_1], void 0, function* (overlapArray, layerData, isCancelled = false) {
    for (let i = 0; i < overlapArray.length; i++) {
        if (i % 20 === 0) {
            if (isCancelled && isCancelled()) {
                return Promise.reject('Cancelled');
            }
        }
        yield new Promise((resolve) => setTimeout(() => {
            const nbOverlapped = overlapArray[i].directions.length;
            let oppositeDirectionOffset = 0;
            let sameDirectionOffset = 0;
            for (let j = 0; j < nbOverlapped; j++) {
                const segment = overlapArray[i].geoData;
                if (overlapArray[i].directions[j]) {
                    const line = layerData.features[overlapArray[i].crossingLines[j]];
                    replaceLineCoordinates(segment, sameDirectionOffset, line);
                    sameDirectionOffset++;
                }
                else {
                    // No deep copy made before the reverse as there was already one previously
                    segment.geometry.coordinates.reverse();
                    const line = layerData.features[overlapArray[i].crossingLines[j]];
                    replaceLineCoordinates(segment, oppositeDirectionOffset, line);
                    oppositeDirectionOffset++;
                }
            }
            resolve();
        }, 0));
    }
    return;
});
/**
 * Replace coordinates of a segment of line with the offsetted coordinates
 * @param originalSegment The unmodified coordinates of the segment to offset
 * @param offsetCount Units by which to offset the segment
 * @param line The complete line on which to apply the offset segment (will be modified)
 */
const replaceLineCoordinates = (originalSegment, offsetCount, line) => {
    const offsetSegment = (0, turf_1.lineOffset)(originalSegment, exports.OFFSET_WIDTH * offsetCount, { units: 'meters' });
    // We go through the coordinates of every single LineString until we reach the starting point of the segment we want to replace
    for (let i = 0; i < line.geometry.coordinates.length; i++) {
        let match = true;
        originalSegment.geometry.coordinates.forEach((oldCoord, index) => {
            if (i + index >= line.geometry.coordinates.length) {
                match = false;
            }
            else {
                const lineCoord = line.geometry.coordinates[i + index];
                if (lineCoord[0] !== oldCoord[0] || lineCoord[1] !== oldCoord[1]) {
                    match = false;
                }
            }
        });
        if (match) {
            for (let j = 0; j < originalSegment.geometry.coordinates.length; j++) {
                line.geometry.coordinates[i + j] = offsetSegment.geometry.coordinates[j];
            }
            break;
        }
    }
};
const cleanLines = (geojson) => {
    geojson.features.forEach((feature) => {
        feature.geometry.coordinates = feature.geometry.coordinates.filter((value) => {
            return !Number.isNaN(value[0]) && !Number.isNaN(value[1]);
        });
    });
};
//# sourceMappingURL=ManageOverlappingLines.js.map